import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import {
	useTranslationCapManage,
	useTranslationCommonForm,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	UpdateSecurityCurrencyFormDto,
	UpdateSecurityCurrencyShape,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/security.zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { CurrencyType } from '@dealstack/shared/lib/generated';
import { updateCurrencyForPersonSecurities } from '@dealstack/frontend/shared/dataExchange/services/securities';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';

export enum UPDATE_SECURITY_CURRENCY_FORM_FIELD {
	currentCurrency = 'currentCurrency',
	newCurrency = 'newCurrency',
}

export const useUpdateSecurityCurrencyDrawer = (
	refreshSecuritiesList: () => void,
	onClose: (createdSecurityId?: string) => void,
	currentCurrency: CurrencyType | undefined
) => {
	const tCapManage = useTranslationCapManage();
	const tCommonForm = useTranslationCommonForm();

	const { selectedEntityId } = useContext(CapManageControllerContext);
	const updateSecurityCurrencyRequest = useMutation({
		mutationFn: updateCurrencyForPersonSecurities,
	});

	const DEFAULT_FORM_VALUES = useMemo(() => {
		return {
			[UPDATE_SECURITY_CURRENCY_FORM_FIELD.currentCurrency]: currentCurrency,
			[UPDATE_SECURITY_CURRENCY_FORM_FIELD.newCurrency]: null,
		} as UpdateSecurityCurrencyFormDto;
	}, [currentCurrency]);

	useEffect(() => {
		if (!currentCurrency) {
			return;
		}

		form.setValue(UPDATE_SECURITY_CURRENCY_FORM_FIELD.currentCurrency, currentCurrency);
	}, [currentCurrency]);

	const form = useForm<UpdateSecurityCurrencyFormDto>({
		mode: 'all',
		resolver: zodResolver(UpdateSecurityCurrencyShape(tCommonForm)),
		defaultValues: DEFAULT_FORM_VALUES,
		shouldFocusError: false,
	});

	const onSubmit = () => (formValues: UpdateSecurityCurrencyFormDto) => {
		const newCurrency = formValues.newCurrency;

		if (!newCurrency) {
			return;
		}

		updateSecurityCurrencyRequest.mutate(
			{
				personId: selectedEntityId,
				dto: {
					currency: newCurrency,
				},
			},
			{
				onSuccess: async () => {
					form.reset({ ...DEFAULT_FORM_VALUES });

					refreshSecuritiesList();
					onClose();

					showToastV2({
						title: tCapManage('toasts.success.security_update_currency'),
					});
				},
			}
		);
	};

	return {
		updateSecurityCurrencyRequest,
		onSubmit,
		form,
	};
};
